'use client';

import React from 'react';
import { useTranslation } from 'next-i18next';
import { Button } from '@components/common/Forms/Button/Button';
import { TailwindSpinner } from '@components/common/Elements/Spinner';
import { AudioPermissionState } from '../types';

interface PermissionActionsProps {
  permissionState: AudioPermissionState;
  onRequestPermissionAction: () => void;
  onStartInterviewAction: () => void;
}

/**
 * PermissionActions
 *
 * Renders an action button (or loading spinner) for microphone permission flow in the
 * audio interview onboarding, based on current permission state.
 *
 * - Shows a button to request microphone permission if not yet granted.
 * - Shows a "Start Interview" button if permission has been granted.
 * - Shows a loading spinner and disables actions when permission is being requested.
 *
 * Props:
 * - permissionState  ('idle' | 'requesting' | 'granted' | 'denied')
 * - onRequestPermission: function to call to trigger permission request
 * - onStartInterview: function to call to start the interview when allowed
 */
export const PermissionActions: React.FC<PermissionActionsProps> = ({
  permissionState,
  onRequestPermissionAction,
  onStartInterviewAction,
}) => {
  const { t } = useTranslation('common');

  return (
    <div className="flex justify-center">
      {(permissionState === 'idle' || permissionState === 'requesting') && (
        <Button
          type="button"
          styling="solid"
          color="primary"
          size="lg"
          rounding="normal"
          onClick={onRequestPermissionAction}
          disabled={permissionState === 'requesting'}
        >
          {permissionState === 'requesting' ? (
            <>
              <TailwindSpinner className="mr-2 size-5" />
              {t('audio-interview.microphone-permission.requesting')}
            </>
          ) : (
            t('audio-interview.microphone-permission.enable')
          )}
        </Button>
      )}
      {permissionState === 'granted' && (
        <Button
          type="button"
          styling="solid"
          color="secondary"
          size="lg"
          rounding="normal"
          onClick={onStartInterviewAction}
        >
          {t('audio-interview.start-interview')}
        </Button>
      )}
    </div>
  );
};

